import { Capacitor, registerPlugin } from '@capacitor/core'
import {
  LocalNotifications,
  type DeliveredNotificationSchema,
  type LocalNotificationDescriptor,
} from '@capacitor/local-notifications'
import type { PluginListenerHandle } from '@capacitor/core'
import { getNativePlatform } from './capacitor'

export type NotificationPermissionResult = 'granted' | 'denied' | 'prompt'

// Custom Settings plugin interface
interface SettingsPlugin {
  openAppSettings(): Promise<void>
}

// Register the custom native Settings plugin
const Settings = registerPlugin<SettingsPlugin>('Settings')

const WORKOUT_REMINDER_OFFSET = 11
const POST_WORKOUT_OFFSET = 29

let listenersInitialized = false
let listenerHandles: PluginListenerHandle[] = []

const isNotificationsSupported = () =>
  getNativePlatform() === 'ios' && Capacitor.isPluginAvailable('LocalNotifications')

const mapPermission = (permission?: string): NotificationPermissionResult => {
  if (permission === 'granted') {
    return 'granted'
  }

  if (permission === 'denied') {
    return 'denied'
  }

  return 'prompt'
}

export const initNotifications = async () => {
  if (!isNotificationsSupported() || listenersInitialized) {
    return
  }

  try {
    const [received, action] = await Promise.all([
      LocalNotifications.addListener('localNotificationReceived', (notification) => {
        console.log('[Notifications] Received:', notification.title)
      }),
      LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
        console.log('[Notifications] Action performed:', event.notification.title)
      }),
    ])

    listenerHandles = [received, action]
    listenersInitialized = true
    console.log('[Notifications] Listeners initialized')
  } catch (error) {
    console.warn('[Notifications] Failed to initialize listeners:', error)
  }
}

export const cleanupNotifications = async () => {
  if (!listenersInitialized) {
    return
  }

  try {
    await Promise.all(listenerHandles.map((handle) => handle.remove()))
    listenerHandles = []
    listenersInitialized = false
  } catch (error) {
    console.warn('[Notifications] Failed to cleanup listeners:', error)
  }
}

export const checkNotificationPermission = async (): Promise<NotificationPermissionResult> => {
  if (!isNotificationsSupported()) {
    return 'denied'
  }

  try {
    const result = await LocalNotifications.checkPermissions()
    return mapPermission(result.display)
  } catch (error) {
    console.warn('[Notifications] Failed to check permission:', error)
    return 'denied'
  }
}

export const requestNotificationPermission = async (): Promise<NotificationPermissionResult> => {
  if (!isNotificationsSupported()) {
    return 'denied'
  }

  try {
    const result = await LocalNotifications.requestPermissions()
    return mapPermission(result.display)
  } catch (error) {
    console.warn('[Notifications] Failed to request permission:', error)
    return 'denied'
  }
}

export const areNotificationsEnabled = async (): Promise<boolean> => {
  if (!isNotificationsSupported()) {
    return false
  }

  try {
    const [permission, enabled] = await Promise.all([
      LocalNotifications.checkPermissions(),
      LocalNotifications.areEnabled(),
    ])

    return permission.display === 'granted' && enabled.value
  } catch (error) {
    console.warn('[Notifications] Failed to check if enabled:', error)
    return false
  }
}

/**
 * Opens the iOS Settings app directly to this app's notification settings.
 * Uses a custom native plugin that calls UIApplication.shared.open()
 */
export const openNotificationSettings = async () => {
  if (!isNotificationsSupported()) {
    return
  }

  try {
    await Settings.openAppSettings()
  } catch (error) {
    console.warn('[Notifications] Failed to open settings:', error)
  }
}

export const scheduleWorkoutReminder = async (
  sessionDate: Date,
  sessionId: string,
  workoutName: string
) => {
  if (!isNotificationsSupported()) {
    return
  }

  const permission = await checkNotificationPermission()

  if (permission !== 'granted') {
    console.log('[Notifications] Permission not granted, skipping schedule')
    return
  }

  const notificationId = hashStringToId(`workout-${sessionId}`)
  const adjustedDate = new Date(sessionDate.getTime() - WORKOUT_REMINDER_OFFSET * 60 * 1000)

  if (adjustedDate.getTime() <= Date.now()) {
    console.log('[Notifications] Session date is in the past, skipping')
    return
  }

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: 'Time to train! 💪',
          body: `Your ${workoutName || 'workout'} is starting now`,
          schedule: {
            at: adjustedDate,
            allowWhileIdle: true,
          },
          sound: 'default',
          extra: {
            sessionId,
            type: 'workout_reminder',
          },
        },
      ],
    })

    console.log(`[Notifications] Scheduled workout reminder for ${adjustedDate.toISOString()}`)
  } catch (error) {
    console.warn('[Notifications] Failed to schedule workout reminder:', error)
  }
}

export const schedulePostWorkoutCelebration = async (sessionId: string) => {
  if (!isNotificationsSupported()) {
    return
  }

  const permission = await checkNotificationPermission()

  if (permission !== 'granted') {
    return
  }

  const notificationId = hashStringToId(`post-workout-${sessionId}`)
  const scheduleDate = new Date(Date.now() + POST_WORKOUT_OFFSET * 60 * 1000)

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: 'Great workout! 🎉',
          body: 'You crushed it today. Rest up and recover.',
          schedule: {
            at: scheduleDate,
            allowWhileIdle: true,
          },
          sound: 'default',
          extra: {
            sessionId,
            type: 'post_workout',
          },
        },
      ],
    })

    console.log(`[Notifications] Scheduled post-workout celebration for ${scheduleDate.toISOString()}`)
  } catch (error) {
    console.warn('[Notifications] Failed to schedule post-workout celebration:', error)
  }
}

export const cancelSessionNotifications = async (sessionId: string) => {
  if (!isNotificationsSupported()) {
    return
  }

  const ids: LocalNotificationDescriptor[] = [
    { id: hashStringToId(`workout-${sessionId}`) },
    { id: hashStringToId(`post-workout-${sessionId}`) },
  ]

  try {
    await LocalNotifications.cancel({ notifications: ids })
    console.log(`[Notifications] Cancelled notifications for session ${sessionId}`)
  } catch (error) {
    console.warn('[Notifications] Failed to cancel session notifications:', error)
  }
}

export const cancelAllNotifications = async () => {
  if (!isNotificationsSupported()) {
    return
  }

  try {
    const pending = await LocalNotifications.getPending()

    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({
        notifications: pending.notifications.map((n) => ({ id: n.id })),
      })
    }

    console.log('[Notifications] Cancelled all pending notifications')
  } catch (error) {
    console.warn('[Notifications] Failed to cancel all notifications:', error)
  }
}

export const getDeliveredNotifications = async (): Promise<DeliveredNotificationSchema[]> => {
  if (!isNotificationsSupported()) {
    return []
  }

  try {
    const result = await LocalNotifications.getDeliveredNotifications()
    return result.notifications
  } catch (error) {
    console.warn('[Notifications] Failed to get delivered notifications:', error)
    return []
  }
}

const hashStringToId = (str: string): number => {
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0
  }

  return Math.abs(hash)
}
